import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Layout from '../components/Layout/Layout';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { adminApi } from '../config/axios';
import { useAuth } from '../contexts/AuthContext';
import { useGlobal } from '../contexts/GlobalContext';
import { useDocumentTitle } from '../config/utils';
import avatar from '../assets/img/unknown_person.jpg';

export default function Account() {
  useDocumentTitle('My Profile');
  const { user, updateUser }   = useAuth();
  const { showToast }          = useGlobal();
  const [preview, setPreview]  = useState(user?.image ?? avatar);
  const [imageFile, setImageFile] = useState(null);

  const { register, handleSubmit, reset, control, formState: { errors, isSubmitting } } = useForm();

  useEffect(() => {
    if (!user) return;
    setPreview(user.image ?? avatar);
    reset({ name: user.name, email: user.email, phone: user.phone ?? '' });
  }, [user]);

  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('phone', data.phone);
    if (imageFile) formData.append('profile_img', imageFile);
    try {
      const res = await adminApi.post('/users/update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser({ name: res.data?.data?.name, image: res.data?.data?.image, phone: res.data?.data?.phone });
      showToast('success', 'Profile Updated', res.data?.data?.msg ?? 'Changes saved.');
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.message ?? 'Update failed.');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  return (
    <Layout>
      <div className="max-w-2xl">
        <h1 className="section-title text-xl mb-6">My Profile</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Avatar */}
          <div className="card-padded flex items-center gap-5">
            <div className="relative">
              <img src={preview} alt="Avatar" className="w-20 h-20 rounded-2xl object-cover border-2 border-white/[0.12]" />
              <label htmlFor="avatar-upload"
                className="absolute -bottom-2 -right-2 w-7 h-7 bg-accent rounded-full flex items-center justify-center cursor-pointer hover:bg-accent-dark transition-colors">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </label>
              <input id="avatar-upload" type="file" accept="image/png,image/jpeg" className="sr-only" onChange={handleImageChange} />
            </div>
            <div>
              <p className="text-text-main font-semibold">{user?.name}</p>
              <p className="text-text-muted text-sm">{user?.email}</p>
              <p className="text-xs text-text-muted mt-2">JPG or PNG, max 2MB</p>
            </div>
          </div>

          {/* Fields */}
          <div className="card-padded space-y-4">
            <Input label="Full Name" error={errors.name?.message}
              {...register('name', { required: 'Name is required' })} />

            <Input label="Email Address" type="email" disabled
              {...register('email')}
              className="opacity-60 cursor-not-allowed" />

            <div>
              <label className="label">Phone Number</label>
              <Controller
                name="phone"
                control={control}
                rules={{ required: 'Phone is required' }}
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    countryCodeEditable={false}
                    inputClass="!w-full !bg-bg-input !border-white/[0.08] !text-text-main !text-sm !rounded-lg !py-2.5 !pl-12"
                    containerClass="!w-full"
                    buttonClass="!bg-bg-input !border-white/[0.08] !rounded-l-lg"
                    dropdownClass="!bg-bg-card !border-white/[0.08] !text-text-main"
                  />
                )}
              />
              {errors.phone && <p className="mt-1 text-xs text-danger">{errors.phone.message}</p>}
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" loading={isSubmitting}>Save Changes</Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
